import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera } from "lucide-react";

const FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39, Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8, Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR, Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.PDF_417, Html5QrcodeSupportedFormats.UPC_A,
];
const CAMERA_ERROR = "Impossibile aprire la fotocamera: consenti l'accesso alla fotocamera nelle impostazioni del browser";

const CameraView = ({ onCode, stopRef }) => {
  const [error, setError] = useState("");
  const last = useRef({ code: "", at: 0 });
  const cb = useRef(onCode);
  cb.current = onCode;

  useEffect(() => {
    let started = Promise.resolve(false);
    let scanner = null;
    // Deferred start avoids a double camera in React StrictMode
    const timer = setTimeout(() => {
      scanner = new Html5Qrcode("camera-reader", {
        formatsToSupport: FORMATS, verbose: false,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      started = scanner.start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: (w, h) => ({ width: Math.floor(w * 0.95), height: Math.floor(Math.min(h, w) * 0.6) }),
          videoConstraints: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: "continuous" }] },
        },
        (text) => {
          const now = Date.now();
          if (text === last.current.code && now - last.current.at < 4000) return;
          last.current = { code: text, at: now };
          cb.current(text);
        },
        () => {}
      ).then(() => true).catch(() => { setError(CAMERA_ERROR); return false; });
    }, 50);
    const stop = () => started.then((ok) => (ok ? scanner.stop().then(() => scanner.clear()) : null)).catch(() => {});
    stopRef.current = stop;
    return () => { clearTimeout(timer); stop(); };
  }, [stopRef]);

  return (
    <div className="space-y-3">
      <div id="camera-reader" className="w-full overflow-hidden rounded-lg bg-black min-h-[240px]" data-testid="camera-reader" />
      {error
        ? <p className="text-sm text-red-600" data-testid="camera-error">{error}</p>
        : <p className="text-sm text-gray-500 text-center">Inquadra il codice a barre: la lettura è automatica e puoi passare subito al pacco successivo</p>}
    </div>
  );
};

export const CameraScannerDialog = ({ open, onClose, onCode }) => {
  const stopRef = useRef(() => Promise.resolve());
  const close = async () => { await stopRef.current(); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="camera-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5" />Scansiona con fotocamera</DialogTitle>
        </DialogHeader>
        {open && <CameraView onCode={onCode} stopRef={stopRef} />}
      </DialogContent>
    </Dialog>
  );
};
